import { useMemo, useState } from 'react';
import { ExpandMoreIcon, HFBox, HFButton, HFPopover, HFSkeleton } from '@hf/designSystem/v1';
import { OMS_ORDER_STATUS_LABELS, OMS_RECEIVED_HINT, omsOrderStatusLabel, type OmsOrder } from '@hf/sharedContract';

/** `statuses` are order status labels. The blotter shows only orders with one of them. */
export type OrderStatusFilter = { name: string; statuses: readonly string[] };

const GROUPS: ReadonlyArray<{ name: string; statuses: readonly string[]; menu?: true }> = [
  { name: 'Preview', statuses: [OMS_ORDER_STATUS_LABELS.Unsubmitted] },
  {
    name: 'Active',
    statuses: [OMS_ORDER_STATUS_LABELS.Submitted, OMS_ORDER_STATUS_LABELS.Pending, OMS_ORDER_STATUS_LABELS.PartiallyFilled],
    menu: true,
  },
  { name: 'Rejected', statuses: [OMS_ORDER_STATUS_LABELS.Rejected] },
  {
    name: 'Finished',
    statuses: [OMS_ORDER_STATUS_LABELS.Filled, OMS_ORDER_STATUS_LABELS.Cancelled, OMS_ORDER_STATUS_LABELS.Expired, OMS_ORDER_STATUS_LABELS.Void],
    menu: true,
  },
];

const COMPACT_BUTTON_SX = { minHeight: 28, px: 1.25, py: 0.25, fontSize: 12 } as const;

function Count({ value, loading }: { value: number | null; loading: boolean }) {
  if (loading) return <HFSkeleton variant="rounded" width={16} height={14} sx={{ ml: 0.75 }} />;
  return (
    <HFBox component="span" sx={{ ml: 0.75, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
      {value == null ? '—' : value}
    </HFBox>
  );
}

/**
 * One row of order counts for the trade day, inside the blotter card. Each status filters the blotter below.
 * Counts come from the same order read the blotter shows. A failed read shows a dash, never zero.
 */
export function OrderStatusStrip({
  orders,
  failed,
  selected,
  onSelect,
}: {
  orders: readonly OmsOrder[] | null;
  failed: boolean;
  selected: OrderStatusFilter | null;
  onSelect: (filter: OrderStatusFilter | null) => void;
}) {
  const [menu, setMenu] = useState<{ name: string; anchor: HTMLElement } | null>(null);
  const counts = useMemo(() => {
    const byLabel = new Map<string, number>();
    for (const order of orders ?? []) {
      const label = omsOrderStatusLabel(order.status);
      byLabel.set(label, (byLabel.get(label) ?? 0) + 1);
    }
    return byLabel;
  }, [orders]);
  const loading = orders === null;
  const unavailable = loading || failed;
  const count = (statuses: readonly string[]): number => statuses.reduce((total, label) => total + (counts.get(label) ?? 0), 0);
  const openGroup = GROUPS.find((group) => group.name === menu?.name);

  const item = (filter: OrderStatusFilter | null, label: string, isSelected: boolean, options: { title?: string; inMenu?: true } = {}) => {
    const value = failed ? null : filter ? count(filter.statuses) : (orders?.length ?? 0);
    return (
      <HFButton
        key={label}
        size="small"
        variant={options.inMenu ? 'text' : isSelected ? 'contained' : 'outlined'}
        role={options.inMenu ? 'menuitem' : undefined}
        aria-pressed={options.inMenu ? undefined : isSelected}
        aria-current={options.inMenu && isSelected ? 'true' : undefined}
        disabled={unavailable}
        title={options.title}
        onClick={() => {
          onSelect(filter);
          setMenu(null);
        }}
        sx={
          options.inMenu
            ? {
                ...COMPACT_BUTTON_SX,
                justifyContent: 'space-between',
                fontWeight: isSelected ? 700 : 500,
                bgcolor: isSelected ? 'action.selected' : undefined,
              }
            : COMPACT_BUTTON_SX
        }
      >
        {label} <Count value={value} loading={loading} />
      </HFButton>
    );
  };

  return (
    <HFBox
      component="section"
      aria-label="Order statuses"
      aria-busy={loading}
      title={failed ? 'Totals unavailable' : undefined}
      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
    >
      <HFBox component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}>
        STATUS
      </HFBox>
      {item(null, 'All', selected === null)}
      {GROUPS.map((group) => {
        const groupSelected = selected != null && (selected.name === group.name || (group.menu === true && group.statuses.includes(selected.name)));
        return (
          <HFBox key={group.name} sx={{ display: 'inline-flex' }}>
            {group.menu ? (
              <>
                <HFButton
                  size="small"
                  variant={groupSelected ? 'contained' : 'outlined'}
                  aria-pressed={groupSelected}
                  disabled={unavailable}
                  onClick={() => onSelect({ name: group.name, statuses: group.statuses })}
                  sx={{
                    ...COMPACT_BUTTON_SX,
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    ...(groupSelected ? { boxShadow: 'none' } : { borderRightWidth: 0 }),
                  }}
                >
                  {group.name} <Count value={failed ? null : count(group.statuses)} loading={loading} />
                </HFButton>
                <HFButton
                  size="small"
                  variant={groupSelected ? 'contained' : 'outlined'}
                  aria-label={`${group.name} statuses`}
                  aria-haspopup="menu"
                  aria-expanded={menu?.name === group.name}
                  disabled={unavailable}
                  onClick={(event) => setMenu(menu?.name === group.name ? null : { name: group.name, anchor: event.currentTarget })}
                  sx={{
                    ...COMPACT_BUTTON_SX,
                    minWidth: 28,
                    px: 0.5,
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    ...(groupSelected ? { boxShadow: 'none' } : {}),
                  }}
                >
                  <ExpandMoreIcon sx={{ fontSize: 18, transform: menu?.name === group.name ? 'rotate(180deg)' : undefined }} />
                </HFButton>
              </>
            ) : (
              item({ name: group.name, statuses: group.statuses }, group.name, groupSelected)
            )}
          </HFBox>
        );
      })}
      <HFPopover
        open={openGroup != null}
        anchorEl={menu?.anchor}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, p: 0.5 } } }}
      >
        <HFBox
          role="menu"
          aria-label={openGroup ? `${openGroup.name} statuses` : undefined}
          sx={{ display: 'flex', flexDirection: 'column', minWidth: 140 }}
        >
          {openGroup?.statuses.map((label) =>
            item({ name: label, statuses: [label] }, label, selected?.name === label, {
              inMenu: true,
              title: label === OMS_ORDER_STATUS_LABELS.Pending ? OMS_RECEIVED_HINT : undefined,
            }),
          )}
        </HFBox>
      </HFPopover>
    </HFBox>
  );
}
